import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaHome,
  FaExclamationTriangle,
  FaClock,
  FaDollarSign,
  FaCalendarAlt,
  FaChartLine,
  FaPlus,
  FaCreditCard
} from 'react-icons/fa';
import axios from 'axios';
import style from './RentalDashboard.module.css';
import NewContractChooser from '../../Views/RentalManagement/NewContractChooser/NewContractChooser';
import RegisterPaymentModal from '../RegisterPaymentModal/RegisterPaymentModal';
import {
  RENTAL_TYPE,
  getDetailPath,
  formatCurrency,
  formatDate,
  typeLabel
} from '../../Views/RentalManagement/utils/rentalTypeHelpers';

const sumNumber = (...vals) =>
  vals.reduce((acc, v) => acc + (Number(v) || 0), 0);

const tagPayments = (list = [], type) =>
  (Array.isArray(list) ? list : []).map((p) => ({ ...p, _type: type }));

const RentalDashboard = ({ data, adminData, onRefresh }) => {
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState(null); // 'contract' | null
  const [activePaymentModal, setActivePaymentModal] = useState(null); // payment row | null
  const [paymentsView, setPaymentsView] = useState('upcoming');
  const [enrichedPayments, setEnrichedPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [tenantModal, setTenantModal] = useState({
    show: false,
    tenants: [],
    loading: false
  });

  // Combinar pagos próximos de regular + admin
  useEffect(() => {
    const regularUpcoming = tagPayments(
      data?.upcomingPayments || [],
      RENTAL_TYPE.REGULAR
    );
    const adminUpcoming = tagPayments(
      adminData?.upcomingPayments || adminData?.nextPayments || [],
      RENTAL_TYPE.ADMIN
    );
    const merged = [...regularUpcoming, ...adminUpcoming].sort((a, b) => {
      const da = new Date(a.dueDate || a.scheduledDate || 0).getTime();
      const db = new Date(b.dueDate || b.scheduledDate || 0).getTime();
      return da - db;
    });
    setEnrichedPayments(merged);
  }, [data, adminData]);

  // Filtros locales
  useEffect(() => {
    const now = new Date();
    const filtered = enrichedPayments.filter((p) => {
      const status = (p.paymentStatus || p.status || '').toLowerCase();
      const due = new Date(p.dueDate || p.scheduledDate);
      const isOverdue = due < now && status !== 'paid';
      if (paymentsView === 'overdue') return isOverdue;
      if (paymentsView === 'upcoming') return !isOverdue && status !== 'paid';
      if (paymentsView === 'pending') {
        const m = now.getMonth();
        const y = now.getFullYear();
        return due.getMonth() === m && due.getFullYear() === y && status !== 'paid';
      }
      return true;
    });
    setFilteredPayments(filtered);
  }, [enrichedPayments, paymentsView]);

  const filterPayments = (view) => setPaymentsView(view);

  // Combinar métricas
  const regSummary = data?.summary || {};
  const admSummary = adminData?.summary || {};
  const summary = {
    activeContracts: sumNumber(
      regSummary.activeContracts,
      admSummary.totalContracts ?? admSummary.activeContracts
    ),
    expiringContracts: sumNumber(
      regSummary.expiringContracts,
      admSummary.expiringContracts
    ),
    overduePayments: sumNumber(
      regSummary.overduePayments,
      admSummary.overduePayments
    ),
    totalOverdue: sumNumber(regSummary.totalOverdue, admSummary.totalOverdue),
    pendingPaymentsInRange: sumNumber(
      regSummary.pendingPaymentsInRange,
      admSummary.pendingPaymentsThisMonth ?? admSummary.pendingPaymentsInRange
    ),
    collectedThisMonth: sumNumber(
      regSummary.collectedThisMonth,
      admSummary.collectedThisMonth
    ),
    upcomingAdjustments: sumNumber(
      regSummary.upcomingAdjustments,
      admSummary.upcomingAdjustments
    ),
    terminatedContracts: sumNumber(
      regSummary.terminatedContracts,
      admSummary.terminatedContracts
    )
  };

  // Información de cada fila normalizada
  const getPaymentInfo = (payment) => {
    const isAdmin = payment._type === RENTAL_TYPE.ADMIN;
    const rentalRef = isAdmin ? payment.adminRental : payment.rental;
    return {
      type: payment._type,
      rentalId: payment.rentalId || payment.adminRentalId || rentalRef?.id,
      contractId:
        payment.contractId ||
        payment.rentalId ||
        payment.adminRentalId ||
        rentalRef?.id,
      propertyAddress:
        rentalRef?.property
          ? `${rentalRef.property.street || ''} ${rentalRef.property.number || ''}, ${rentalRef.property.locality || ''}`.trim()
          : rentalRef?.propertyAddress || payment.propertyAddress || 'Sin dirección',
      propertyId: rentalRef?.property?.id ?? rentalRef?.propertyId ?? '—',
      propertyType: rentalRef?.property?.propertyType || '—',
      ownerName:
        rentalRef?.ownerName ||
        rentalRef?.owner?.name ||
        rentalRef?.owner?.fullName ||
        payment.ownerName ||
        'Sin propietario',
      dueDate: payment.dueDate || payment.scheduledDate,
      totalAmount: payment.totalAmount ?? payment.amount ?? rentalRef?.monthlyAmount,
      status:
        payment.paymentStatus ||
        payment.status ||
        (payment.isPaid ? 'paid' : 'pending')
    };
  };

  // Tenant modal — branchea por tipo
  const fetchTenants = async (rentalId, type) => {
    if (!rentalId) return;
    setTenantModal({ show: true, tenants: [], loading: true });
    try {
      const url =
        type === RENTAL_TYPE.ADMIN
          ? `/admin-rentals/${rentalId}/tenants`
          : `/rentals/admin/${rentalId}/tenants`;
      const response = await axios.get(url);
      const tenants = response.data?.tenants || response.data?.data || [];
      setTenantModal({ show: true, tenants, loading: false });
    } catch (err) {
      console.error('Error fetching tenants:', err);
      setTenantModal({ show: true, tenants: [], loading: false });
    }
  };

  if (!data && !adminData) return null;

  const handleContractCreated = () => {
    setActiveModal(null);
    if (typeof onRefresh === 'function') onRefresh();
  };

  const handlePaymentRegistered = () => {
    setActivePaymentModal(null);
    if (typeof onRefresh === 'function') onRefresh();
  };

  return (
    <div className={style.dashboard}>
      <div className={style.metricsGrid}>
        <div className={`${style.metricCard} ${style.active}`}>
          <div className={style.cardHeader}>
            <FaHome className={style.cardIcon} />
            <h3>Contratos Activos</h3>
          </div>
          <div className={style.cardValue}>{summary.activeContracts}</div>
          <Link to="/alquileres/contratos" className={style.cardLink}>
            Ver todos →
          </Link>
        </div>

        <div
          className={`${style.metricCard} ${style.warning} ${style.clickableCard}`}
          role="button"
          tabIndex={0}
          onClick={() => navigate('/alquileres/por-vencer')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/alquileres/por-vencer');
            }
          }}
        >
          <div className={style.cardHeader}>
            <FaExclamationTriangle className={style.cardIcon} />
            <h3>Por Vencer</h3>
          </div>
          <div className={style.cardValue}>{summary.expiringContracts}</div>
          <Link
            to="/alquileres/por-vencer"
            className={style.cardLink}
            onClick={(e) => e.stopPropagation()}
          >
            Ver detalles →
          </Link>
        </div>

        <div
          className={`${style.metricCard} ${style.danger} ${style.clickableCard}`}
          role="button"
          tabIndex={0}
          onClick={() => navigate('/alquileres/vencidos')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/alquileres/vencidos');
            }
          }}
        >
          <div className={style.cardHeader}>
            <FaClock className={style.cardIcon} />
            <h3>Pagos Vencidos</h3>
          </div>
          <div className={style.cardValue}>{summary.overduePayments}</div>
          <div className={style.cardSubvalue}>
            {formatCurrency(summary.totalOverdue)}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate('/alquileres/vencidos');
            }}
            className={style.cardLink}
          >
            Gestionar →
          </button>
        </div>

        <div
          className={`${style.metricCard} ${style.pending} ${style.clickableCard}`}
          role="button"
          tabIndex={0}
          onClick={() => navigate('/alquileres/pendientes-mes')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/alquileres/pendientes-mes');
            }
          }}
        >
          <div className={style.cardHeader}>
            <FaCalendarAlt className={style.cardIcon} />
            <h3>Pendientes Este Mes</h3>
          </div>
          <div className={style.cardValue}>{summary.pendingPaymentsInRange}</div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate('/alquileres/pendientes-mes');
            }}
            className={style.cardLink}
          >
            Ver lista →
          </button>
        </div>

        <div className={`${style.metricCard} ${style.success}`}>
          <div className={style.cardHeader}>
            <FaDollarSign className={style.cardIcon} />
            <h3>Cobrado Este Mes</h3>
          </div>
          <div className={style.cardValue}>
            {formatCurrency(summary.collectedThisMonth)}
          </div>
          <Link to="/alquileres/reportes" className={style.cardLink}>
            Ver reportes →
          </Link>
        </div>

        <div className={`${style.metricCard} ${style.info}`}>
          <div className={style.cardHeader}>
            <FaChartLine className={style.cardIcon} />
            <h3>Próximos Ajustes</h3>
          </div>
          <div className={style.cardValue}>{summary.upcomingAdjustments}</div>
          <Link to="/alquileres/ajustes" className={style.cardLink}>
            Gestionar ajustes →
          </Link>
        </div>

        <div className={`${style.metricCard} ${style.warning}`}>
          <div className={style.cardHeader}>
            <FaExclamationTriangle className={style.cardIcon} />
            <h3>Contratos Cancelados</h3>
          </div>
          <div className={style.cardValue}>{summary.terminatedContracts}</div>
          <Link to="/alquileres/contratos" className={style.cardLink}>
            Ver historial →
          </Link>
        </div>
      </div>

      <div className={style.quickActions}>
        <h3>Acciones Rápidas</h3>
        <div className={style.actionGrid}>
          <button
            type="button"
            onClick={() => setActiveModal('contract')}
            className={style.actionButton}
          >
            <FaPlus /> Nuevo Contrato
          </button>
        </div>
      </div>

      {enrichedPayments.length > 0 && (
        <div className={style.upcomingPayments}>
          <div className={style.paymentsHeader}>
            <h3>
              {paymentsView === 'upcoming' && 'Próximos Pagos'}
              {paymentsView === 'overdue' && 'Pagos Vencidos'}
              {paymentsView === 'pending' && 'Pagos Pendientes del Mes'}
            </h3>
            <div className={style.viewFilters}>
              <button
                className={`${style.filterBtn} ${paymentsView === 'upcoming' ? style.active : ''}`}
                onClick={() => filterPayments('upcoming')}
              >
                Por Vencer
              </button>
              <button
                className={`${style.filterBtn} ${paymentsView === 'overdue' ? style.active : ''}`}
                onClick={() => filterPayments('overdue')}
              >
                Vencidos
              </button>
              <button
                className={`${style.filterBtn} ${paymentsView === 'pending' ? style.active : ''}`}
                onClick={() => filterPayments('pending')}
              >
                Pendientes del Mes
              </button>
            </div>
          </div>

          <div className={style.paymentsTable}>
            <div className={style.tableHeader}>
              <span>Propiedad</span>
              <span>Propietario</span>
              <span>Inquilinos</span>
              <span>Vencimiento</span>
              <span>Monto</span>
              <span>Estado</span>
              <span>Acciones</span>
            </div>
            {filteredPayments.length > 0 ? (
              filteredPayments.slice(0, 10).map((payment, index) => {
                const info = getPaymentInfo(payment);
                const isOverdue =
                  new Date(info.dueDate) < new Date() && info.status === 'pending';
                const displayStatus = isOverdue ? 'overdue' : info.status;
                const isAdmin = payment._type === RENTAL_TYPE.ADMIN;

                return (
                  <div
                    key={`${payment._type}-${payment.id || index}`}
                    className={`${style.tableRow} ${isOverdue ? style.overdueRow : ''}`}
                  >
                    <div className={style.property}>
                      <div className={style.propertyTitle}>
                        <span
                          className={`${style.typeBadge} ${isAdmin ? style.admin : ''}`}
                          title={typeLabel(payment._type)}
                        >
                          {isAdmin ? 'Admin' : 'Regular'}
                        </span>
                        ID: {info.propertyId}
                      </div>
                      <div className={style.propertyDetails}>
                        <span className={style.propertyAddress}>
                          📍 {info.propertyAddress}
                        </span>
                        <span className={style.propertyType}>
                          🏠 {info.propertyType}
                        </span>
                      </div>
                    </div>
                    <span className={style.owner}>{info.ownerName}</span>
                    <div className={style.tenantActions}>
                      <button
                        onClick={() => fetchTenants(info.rentalId, payment._type)}
                        className={style.tenantsButton}
                      >
                        Ver Inquilinos
                      </button>
                    </div>
                    <span className={style.dueDate}>
                      {formatDate(info.dueDate)}
                    </span>
                    <span className={style.amount}>
                      {formatCurrency(info.totalAmount)}
                    </span>
                    <span className={`${style.status} ${style[displayStatus] || ''}`}>
                      {displayStatus === 'pending'
                        ? 'Pendiente'
                        : displayStatus === 'overdue'
                        ? 'Vencido'
                        : displayStatus === 'paid'
                        ? 'Pagado'
                        : displayStatus}
                    </span>
                    <div className={style.actionButtons}>
                      {info.contractId && (
                        <Link
                          to={getDetailPath(info.contractId, payment._type)}
                          className={style.payButton}
                          title="Ver contrato"
                        >
                          <FaHome />
                        </Link>
                      )}
                      {displayStatus !== 'paid' && payment.id && (
                        <button
                          onClick={() => setActivePaymentModal(payment)}
                          className={style.payButton}
                          title="Registrar pago"
                        >
                          <FaCreditCard />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={style.noData}>
                No hay{' '}
                {paymentsView === 'upcoming'
                  ? 'pagos próximos'
                  : paymentsView === 'overdue'
                  ? 'pagos vencidos'
                  : 'pagos pendientes'}{' '}
                para mostrar
              </div>
            )}
          </div>
        </div>
      )}

      {tenantModal.show && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <div className={style.modalHeader}>
              <h3>Lista de Inquilinos</h3>
              <button
                onClick={() =>
                  setTenantModal({ show: false, tenants: [], loading: false })
                }
                className={style.closeButton}
              >
                ×
              </button>
            </div>
            <div className={style.modalContent}>
              {tenantModal.loading ? (
                <p>Cargando inquilinos...</p>
              ) : tenantModal.tenants.length > 0 ? (
                <div className={style.tenantsList}>
                  {tenantModal.tenants.map((tenant, index) => (
                    <div key={tenant.id || index} className={style.tenantItem}>
                      <div className={style.tenantInfo}>
                        <strong>
                          {tenant.fullName ||
                            `${tenant.firstName || ''} ${tenant.lastName || ''}`.trim()}
                        </strong>
                        <span className={style.tenantType}>
                          {tenant.tenantType === 'main'
                            ? 'Inquilino Principal'
                            : tenant.tenantType === 'guarantor'
                            ? 'Garante'
                            : tenant.tenantType === 'co-tenant'
                            ? 'Co-inquilino'
                            : tenant.tenantType}
                        </span>
                        {tenant.email && <span>📧 {tenant.email}</span>}
                        {tenant.phone && <span>📞 {tenant.phone}</span>}
                        {tenant.dni && <span>🆔 DNI: {tenant.dni}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No hay inquilinos registrados para este contrato.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeModal === 'contract' && (
        <NewContractChooser
          onClose={() => setActiveModal(null)}
          onSuccess={handleContractCreated}
        />
      )}

      {activePaymentModal && (
        <RegisterPaymentModal
          paymentId={activePaymentModal.id}
          paymentInfo={getPaymentInfo(activePaymentModal)}
          type={activePaymentModal._type}
          onClose={() => setActivePaymentModal(null)}
          onRegistered={handlePaymentRegistered}
        />
      )}
    </div>
  );
};

export default RentalDashboard;
